import { Paper, Text } from '@mantine/core';
import React, { useState, useEffect, useId } from 'react';
import { useLanguage } from '../../../../../../../../stores/LanguageStore';

const AttachmentPreview = ({ attachment }) => {
    const lang = useLanguage(s => s.language)
    // État pour savoir si une erreur de chargement s'est produite
    const [hasError, setHasError] = useState(false);

    // Identifiant unique pour ce composant (utile s'il y a plusieurs AttachmentPreview sur la page)
    const uniqueId = useId();

    // Écouteur pour les messages provenant des iframes (Audio / Vidéo)
    useEffect(() => {
        const handleMessage = (event) => {
            // Si le message est une erreur et correspond à l'ID de ce composant
            if (event.data?.type === 'ATTACHMENT_LOAD_ERROR' && event.data?.id === uniqueId) {
                setHasError(true);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [uniqueId]);

    const getPreview = (attachment) => {
        const url = attachment?.download_url || attachment?.file_url;
        if (!url) return null;

        // Si une erreur est détectée, on affiche le message demandé
        if (hasError) {
            return (
                <Paper p={"xs"} withBorder bg={"red.0"}>
                    <Text size='xs' maw={340} c="red.7">
                        {lang.prm.file_401}
                    </Text>
                </Paper>
            );
        }

        // 1. Image Preview
        if (attachment?.type === "vectorImage" || url.endsWith(".gif") || url.endsWith(".jpg") || url.endsWith(".jpeg") || url.endsWith(".png")) {
            return (
                <img
                    src={url}
                    alt=""
                    style={{ width: "100%", maxHeight: 200, objectFit: 'contain', marginTop: 8 }}
                    referrerPolicy="no-referrer"
                    onError={() => setHasError(true)} // Détection d'erreur directe pour l'image
                />
            );
        }

        // 2. Audio Preview
        const isAudio = attachment?.type === "audio" || /\.(mp3|wav|m4a|ogg)$/i.test(url);
        if (isAudio) {
            const audioHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta name="referrer" content="no-referrer" />
          <style>
            body { margin: 0; overflow: hidden; display: flex; align-items: center; background: transparent; }
            audio { width: 100%; }
          </style>
        </head>
        <body>
          <audio controls src="${url}"></audio>
          <script>
            // Envoi d'un message au composant parent si l'audio échoue à charger
            document.querySelector('audio').addEventListener('error', function() {
              window.parent.postMessage({ type: 'ATTACHMENT_LOAD_ERROR', id: '${uniqueId}' }, '*');
            });
          </script>
        </body>
      </html>
    `;

            return (
                <iframe
                    srcDoc={audioHtml}
                    style={{ width: "100%", border: "none", height: "54px" }}
                    title="audio-preview"
                />
            );
        }

        // 3. Video Preview
        const isVideo = attachment?.type === "video" || /\.(mp4|webm|ogv|mov)$/i.test(url);
        if (isVideo) {
            const videoHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta name="referrer" content="no-referrer" />
          <style>
            body { margin: 0; background: #000; display: flex; justify-content: center; align-items: center; height: 100vh; }
            video { max-width: 100%; max-height: 100%; }
          </style>
        </head>
        <body>
          <video controls src="${url}"></video>
          <script>
            // Envoi d'un message au composant parent si la vidéo échoue à charger
            document.querySelector('video').addEventListener('error', function() {
              window.parent.postMessage({ type: 'ATTACHMENT_LOAD_ERROR', id: '${uniqueId}' }, '*');
            });
          </script>
        </body>
      </html>
    `;

            return (
                <iframe
                    srcDoc={videoHtml}
                    style={{ width: "100%", border: "none", aspectRatio: "16 / 9", borderRadius: "4px", marginTop: "8px" }}
                    title="video-preview"
                    allowFullScreen
                />
            );
        }

        return null;
    };

    return getPreview(attachment);
}

export default AttachmentPreview;